import React, { useState, useEffect, useCallback, useRef } from 'react';
import { reservationAPI, customerAPI, smsAPI, treatmentAPI } from '../api/axios';
import { combineDateAndTime, formatDate, formatDateKorean, getDayOfWeekKorean, formatCurrency } from '../utils/dateUtils';
import Layout from '../components/Layout';
import Button from '../components/Button';
import Calendar from '../components/Calendar';
import TimeSlots from '../components/TimeSlots';
import './ReservationPage.css';

const MOBILE_BREAKPOINT = 768;

const ReservationPage = () => {
  // 반응형 상태
  const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_BREAKPOINT);

  // PC용 위자드 단계 관리 (1: 날짜/시간, 2: 정보입력, 3: 완료)
  const [step, setStep] = useState(1);

  // 모바일용 세분화된 단계 관리 (1~9)
  // 1: 날짜, 2: 시간, 3: 휴대폰, 4: 이름, 5: 시술, 6: 요청사항, 7: 확인, 8: 동의서, 9: 완료
  const [mobileStep, setMobileStep] = useState(1);

  // Step 1 상태
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [slotsError, setSlotsError] = useState('');

  // Step 2 상태 - 고객 조회 프로세스
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [isExistingCustomer, setIsExistingCustomer] = useState(false);
  const [isCheckingPhone, setIsCheckingPhone] = useState(false);
  const [visitCount, setVisitCount] = useState(null); // null=미확인, 0=첫방문, 1+=재방문

  // SMS 인증 상태 (신규 고객용)
  const [smsVerified, setSmsVerified] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [isSendingSms, setIsSendingSms] = useState(false);
  const [isVerifyingSms, setIsVerifyingSms] = useState(false);
  const [smsSentMessage, setSmsSentMessage] = useState('');
  const [smsError, setSmsError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [showSmsToast, setShowSmsToast] = useState(false);

  // 중복 예약 체크 상태
  const [duplicateReservation, setDuplicateReservation] = useState(null);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);

  const [formData, setFormData] = useState({
    phone: '',
    name: '',
    treatment: '',
    requestMemo: '',
    // 신규 고객 동의 정보
    agreePrivacy: false,
    agreeNotification: false,
    agreeMarketing: false,
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [submitMessage, setSubmitMessage] = useState('');
  const [welcomeMessage, setWelcomeMessage] = useState('');

  // 예약 완료 정보
  const [completedReservation, setCompletedReservation] = useState(null);

  // 애니메이션 방향 상태
  const [slideDirection, setSlideDirection] = useState('next');

  // 모바일 스텝 컨테이너 ref (단계 전환 시 스크롤 상단 이동용)
  const stepContainerRef = useRef(null);

  // 시술 메뉴 옵션 (API에서 가져옴)
  const [treatmentOptions, setTreatmentOptions] = useState([
    { value: '', label: '시술을 선택해주세요', price: 0, duration: '', category: '' }
  ]);
  // eslint-disable-next-line no-unused-vars
  const [_isLoadingTreatments, setIsLoadingTreatments] = useState(true);

  // 모바일 단계 정보
  const mobileStepInfo = {
    1: { title: '날짜 선택', description: '예약 날짜를 선택해주세요' },
    2: { title: '시간 선택', description: '예약 시간을 선택해주세요' },
    3: { title: '연락처 확인', description: '휴대폰 번호를 입력해주세요' },
    4: { title: '이름 입력', description: '예약자 성함을 입력해주세요' },
    5: { title: '시술 선택', description: '시술 메뉴를 선택해주세요' },
    6: { title: '요청사항', description: '전달 사항을 입력해주세요' },
    7: { title: '예약 확인', description: '예약 정보를 확인해주세요' },
    8: { title: '약관 동의', description: '약관을 확인하고 동의해주세요' },
    9: { title: '예약 완료', description: '' },
  };

  // 반응형 리사이즈 핸들러
  useEffect(() => {
    const handleResize = () => {
      const newIsMobile = window.innerWidth <= MOBILE_BREAKPOINT;
      setIsMobile(newIsMobile);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 시술 목록 불러오기 (API)
  useEffect(() => {
    const fetchTreatments = async () => {
      try {
        setIsLoadingTreatments(true);
        const response = await treatmentAPI.getActive();

        // API 응답을 treatmentOptions 형식으로 변환
        const treatments = response.data.map(t => ({
          value: t.name,
          label: t.label,
          price: t.currentPrice, // 현재 적용 가격 (이벤트가 or 정상가)
          regularPrice: t.regularPrice,
          eventPrice: t.eventPrice,
          isEvent: t.useEventPrice, // 이벤트 가격 사용 중인지
          duration: t.duration,
          category: t.category
        }));

        // 맨 앞에 "시술을 선택해주세요" 추가
        setTreatmentOptions([
          { value: '', label: '시술을 선택해주세요', price: 0, duration: '', category: '' },
          ...treatments
        ]);
      } catch (error) {
        console.error('시술 목록 불러오기 실패:', error);
        // 에러 시 기본값 유지
      } finally {
        setIsLoadingTreatments(false);
      }
    };

    fetchTreatments();
  }, []);

  // 모바일 히스토리 상태 관리
  const updateHistoryState = useCallback((newStep) => {
    if (isMobile) {
      window.history.pushState({ mobileStep: newStep }, '', `#step-${newStep}`);
    }
  }, [isMobile]);

  // popstate 이벤트 핸들러 (브라우저 뒤로가기)
  useEffect(() => {
    const handlePopState = (event) => {
      if (isMobile && event.state?.mobileStep) {
        setSlideDirection('prev');
        setMobileStep(event.state.mobileStep);
      } else if (isMobile && mobileStep > 1) {
        // state가 없으면 이전 단계로
        setSlideDirection('prev');
        setMobileStep(prev => Math.max(1, prev - 1));
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isMobile, mobileStep]);

  // 초기 히스토리 상태 설정
  useEffect(() => {
    if (isMobile) {
      window.history.replaceState({ mobileStep: 1 }, '', '#step-1');
    }
  }, [isMobile]);

  // 모바일 단계 전환 시 스크롤 상단 이동
  useEffect(() => {
    if (isMobile && stepContainerRef.current) {
      stepContainerRef.current.scrollTop = 0;
    }
  }, [mobileStep, isMobile]);

  // SMS 재발송 쿨다운 타이머
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // SMS 토스트 자동 숨김 (3초 후)
  useEffect(() => {
    if (showSmsToast) {
      const timer = setTimeout(() => {
        setShowSmsToast(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showSmsToast]);

  // 선택된 시술 정보 가져오기
  const getSelectedTreatment = () => {
    return treatmentOptions.find(t => t.value === formData.treatment) || null;
  };

  // 첫 방문 고객 여부 확인
  const isFirstVisit = () => {
    return visitCount !== null && visitCount === 0;
  };

  // 첫 방문 할인 가격 계산
  const calculateFirstVisitPrice = (treatment) => {
    if (!treatment || !isFirstVisit()) return null;

    // "상담 후 선택"은 할인 제외
    if (treatment.value === '상담 후 선택' || treatment.price === 0) {
      return null;
    }

    // 50% 할인
    const discountedPrice = Math.floor(treatment.regularPrice * 0.5);

    // 기존 이벤트 가격과 비교 (더 저렴한 것 적용)
    if (treatment.isEvent && treatment.eventPrice) {
      return Math.min(discountedPrice, treatment.eventPrice);
    }

    return discountedPrice;
  };

  // 시술 카드에 표시할 가격 정보 반환
  const getTreatmentPriceInfo = (treatment) => {
    // 재방문 고객일 경우 가격 숨김
    if (visitCount !== null && visitCount > 0) {
      return {
        showPrice: false,
        showDiscount: false,
        originalPrice: 0,
        finalPrice: 0,
        discountRate: 0,
        isFirstVisit: false,
      };
    }

    const firstVisitPrice = calculateFirstVisitPrice(treatment);

    if (firstVisitPrice !== null) {
      return {
        showPrice: true,
        showDiscount: true,
        originalPrice: treatment.regularPrice,
        finalPrice: firstVisitPrice,
        discountRate: 50,
        isFirstVisit: true,
      };
    }

    // 기존 이벤트 가격 로직
    if (treatment.isEvent && treatment.eventPrice) {
      return {
        showPrice: true,
        showDiscount: true,
        originalPrice: treatment.regularPrice,
        finalPrice: treatment.eventPrice,
        discountRate: Math.round((1 - treatment.eventPrice / treatment.regularPrice) * 100),
        isFirstVisit: false,
      };
    }

    return {
      showPrice: true,
      showDiscount: false,
      originalPrice: treatment.price,
      finalPrice: treatment.price,
      discountRate: 0,
      isFirstVisit: false,
    };
  };

  // 전화번호 자동 포맷팅
  const formatPhoneNumber = (value) => {
    const numbers = value.replace(/[^0-9]/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 7) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
  };

  // 날짜 선택 핸들러
  const handleDateSelect = async (date) => {
    setSelectedDate(date);
    setSelectedTime('');
    setSlotsError('');
    await fetchAvailableSlots(date);

    // 모바일에서 날짜 선택 시 자동으로 다음 단계로
    if (isMobile) {
      setSlideDirection('next');
      setMobileStep(2);
      updateHistoryState(2);
    }
  };

  // 가용 시간 슬롯 조회 API 호출
  const fetchAvailableSlots = async (date) => {
    if (!date) return;

    setIsLoadingSlots(true);
    setSlotsError('');

    try {
      // 로컬 시간 기준으로 날짜 포맷 (UTC 변환 방지)
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;

      console.log('🔍 가용 시간 조회 요청:', formattedDate);
      const response = await reservationAPI.getAvailableSlots(formattedDate);
      console.log('✅ 가용 시간 API 응답:', response.data);
      console.log('📋 availableSlots:', response.data.availableSlots);
      console.log('🚫 bookedSlots:', response.data.bookedSlots);
      setAvailableSlots(response.data.availableSlots);
    } catch (error) {
      console.error('가용 시간 조회 오류:', error);
      setSlotsError('가용 시간을 불러오는데 실패했습니다. 다시 시도해주세요.');
      setAvailableSlots([]);
    } finally {
      setIsLoadingSlots(false);
    }
  };

  // 시간 선택 핸들러
  const handleTimeSelect = (time) => {
    setSelectedTime(time);

    // 모바일에서 시간 선택 시 자동으로 다음 단계로
    if (isMobile) {
      setSlideDirection('next');
      setMobileStep(3);
      updateHistoryState(3);
    }
  };

  // PC용 다음 단계로 이동
  const handleNextStep = () => {
    if (selectedDate && selectedTime) {
      setStep(2);
    }
  };

  // PC용 이전 단계로 이동
  const handlePrevStep = () => {
    setStep(1);
    resetFormState();
  };

  // 모바일용 다음 단계 이동
  const handleMobileNextStep = () => {
    if (mobileStep < 9) {
      setSlideDirection('next');
      let nextStep = mobileStep + 1;

      // Step 7(예약 확인) 이후, 기존 고객이면 Step 8(동의서)을 건너뛰고 바로 Step 9(완료)로
      if (mobileStep === 7 && isExistingCustomer) {
        nextStep = 9; // 동의서 건너뛰기
      }

      setMobileStep(nextStep);
      updateHistoryState(nextStep);
    }
  };

  // 모바일용 이전 단계 이동
  const handleMobilePrevStep = () => {
    if (mobileStep > 1) {
      setSlideDirection('prev');
      setMobileStep(prev => prev - 1);
      window.history.back();
    }
  };

  // 폼 상태 초기화
  const resetFormState = () => {
    setPhoneVerified(false);
    setIsExistingCustomer(false);
    setWelcomeMessage('');
    setVisitCount(0);
    setSmsVerified(false);
    setVerificationCode('');
    setSmsSentMessage('');
    setSmsError('');
    setResendCooldown(0);
    setShowSmsToast(false);
    setFormData({
      phone: '',
      name: '',
      treatment: '',
      requestMemo: '',
      agreePrivacy: false,
      agreeNotification: false,
      agreeMarketing: false,
    });
    setErrors({});
    setSubmitStatus(null);
    setSubmitMessage('');
  };

  // 전화번호 리셋 핸들러
  const handleResetPhone = () => {
    setPhoneVerified(false);
    setIsExistingCustomer(false);
    setWelcomeMessage('');
    setVisitCount(0);
    setSmsVerified(false);
    setVerificationCode('');
    setSmsSentMessage('');
    setSmsError('');
    setResendCooldown(0);
    setShowSmsToast(false);
    setDuplicateReservation(null);
    setShowDuplicateWarning(false);
    setFormData(prev => ({
      ...prev,
      name: '',
      treatment: '',
      requestMemo: '',
      agreePrivacy: false,
      agreeNotification: false,
      agreeMarketing: false,
    }));
    if (isMobile) {
      setMobileStep(3);
    }
  };

  // 중복 예약 경고 무시하고 계속 진행
  const handleContinueDespiteDuplicate = () => {
    setShowDuplicateWarning(false);
    // 기존 로직 실행 (중복 체크 없이)
    handlePhoneCheckWithoutDuplicateCheck();
  };

  // 중복 체크 없이 휴대폰 번호 확인 (내부용)
  const handlePhoneCheckWithoutDuplicateCheck = async () => {
    setIsCheckingPhone(true);
    setErrors({});

    try {
      const response = await customerAPI.checkByPhone(formData.phone);
      const customerData = response.data;

      if (customerData.exists) {
        // 기존 고객
        setIsExistingCustomer(true);
        setVisitCount(customerData.visitCount ?? 0);
        setFormData(prev => ({
          ...prev,
          name: customerData.name,
        }));
        setWelcomeMessage(`${customerData.name}님`);
        setPhoneVerified(true);
        setSmsVerified(true);

        if (isMobile) {
          setSlideDirection('next');
          setMobileStep(5);
          updateHistoryState(5);
        }
      } else {
        // 신규 고객
        setIsExistingCustomer(false);
        setVisitCount(0); // 신규 고객은 visitCount = 0
        setWelcomeMessage('신규 고객입니다');
        setPhoneVerified(true);

        try {
          await smsAPI.sendVerification(formData.phone);
          setSmsSentMessage('인증번호가 발송되었습니다. (유효시간 3분)');
          setShowSmsToast(true);
          setResendCooldown(180);
        } catch (smsError) {
          console.error('SMS 발송 오류:', smsError);
          if (smsError.response?.data?.message) {
            setSmsError(smsError.response.data.message);
          } else {
            setSmsError('인증번호 발송에 실패했습니다.');
          }
        }
      }
    } catch (error) {
      console.error('고객 조회 오류:', error);
      setErrors({ phone: '고객 정보 조회 중 오류가 발생했습니다' });
    } finally {
      setIsCheckingPhone(false);
    }
  };

  // 입력값 변경 핸들러
  const handleChange = (e) => {
    const { name, value } = e.target;

    // 전화번호 자동 포맷팅
    if (name === 'phone') {
      const formatted = formatPhoneNumber(value);
      setFormData(prev => ({ ...prev, phone: formatted }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    if (submitStatus) {
      setSubmitStatus(null);
      setSubmitMessage('');
    }
  };

  // 중복 예약 체크 함수
  const checkDuplicateReservation = async (phone) => {
    try {
      const response = await reservationAPI.getMyReservations(phone);
      const myReservations = response.data;

      if (!myReservations || myReservations.length === 0) {
        return null;
      }

      // 선택한 날짜와 같은 날짜의 예약 찾기 (PENDING, CONFIRMED 상태만)
      const selectedDateStr = formatDate(selectedDate);
      const duplicates = myReservations.filter(reservation => {
        const reservationDate = reservation.reservationTime.split('T')[0];
        const status = reservation.status;
        return reservationDate === selectedDateStr &&
               (status === 'PENDING' || status === 'CONFIRMED');
      });

      return duplicates.length > 0 ? duplicates[0] : null;
    } catch (error) {
      console.error('예약 조회 오류:', error);
      return null;
    }
  };

  // 휴대폰 번호 확인 핸들러
  const handlePhoneCheck = async () => {
    const phoneNumbers = formData.phone.replace(/[^0-9]/g, '');

    if (!phoneNumbers) {
      setErrors({ phone: '연락처를 입력해주세요' });
      return;
    }

    if (phoneNumbers.length < 10 || phoneNumbers.length > 11) {
      setErrors({ phone: '올바른 연락처 형식이 아닙니다 (10~11자리)' });
      return;
    }

    setIsCheckingPhone(true);
    setErrors({});
    setDuplicateReservation(null);
    setShowDuplicateWarning(false);

    try {
      const response = await customerAPI.checkByPhone(formData.phone);
      const customerData = response.data;

      // 🔍 중복 예약 체크
      const duplicate = await checkDuplicateReservation(formData.phone);
      if (duplicate) {
        setDuplicateReservation(duplicate);
        setShowDuplicateWarning(true);
        setIsCheckingPhone(false);
        return; // 중복 예약이 있으면 여기서 중단
      }

      if (customerData.exists) {
        // 기존 고객
        setIsExistingCustomer(true);
        setVisitCount(customerData.visitCount ?? 0);
        setFormData(prev => ({
          ...prev,
          name: customerData.name,
        }));
        setWelcomeMessage(`${customerData.name}님`);
        setPhoneVerified(true);
        setSmsVerified(true); // 기존 고객은 SMS 인증 불필요

        // 모바일에서 기존 고객이면 시술 선택 단계로 (Step 5)
        if (isMobile) {
          setSlideDirection('next');
          setMobileStep(5);
          updateHistoryState(5);
        }
      } else {
        // 신규 고객 - SMS 인증 필요
        setIsExistingCustomer(false);
        setVisitCount(0); // 신규 고객은 visitCount = 0
        setWelcomeMessage('신규 고객입니다');
        setPhoneVerified(true);

        // 신규 고객에게 자동으로 SMS 발송
        try {
          await smsAPI.sendVerification(formData.phone);
          setSmsSentMessage('인증번호가 발송되었습니다. (유효시간 3분)');
          setShowSmsToast(true);
          setResendCooldown(180); // 3분 쿨다운
        } catch (smsError) {
          console.error('SMS 발송 오류:', smsError);
          if (smsError.response?.data?.message) {
            setSmsError(smsError.response.data.message);
          } else {
            setSmsError('인증번호 발송에 실패했습니다.');
          }
        }

        // 모바일에서 신규 고객이면 SMS 인증 대기 (Step 3 유지)
        // SMS 인증 UI가 Step 3에 표시됨
      }
    } catch (error) {
      console.error('고객 조회 오류:', error);
      setErrors({ phone: '고객 정보 조회 중 오류가 발생했습니다' });
    } finally {
      setIsCheckingPhone(false);
    }
  };

  // SMS 인증 코드 발송
  const handleSendSms = async () => {
    setIsSendingSms(true);
    setSmsError('');
    setSmsSentMessage('');

    try {
      await smsAPI.sendVerification(formData.phone);
      setSmsSentMessage('인증번호가 발송되었습니다. (유효시간 3분)');
      setShowSmsToast(true);
      setResendCooldown(180); // 3분 쿨다운
    } catch (error) {
      console.error('SMS 발송 오류:', error);
      if (error.response?.data?.message) {
        setSmsError(error.response.data.message);
      } else {
        setSmsError('인증번호 발송에 실패했습니다.');
      }
    } finally {
      setIsSendingSms(false);
    }
  };

  // SMS 인증 코드 검증
  const handleVerifySms = async () => {
    if (!verificationCode || verificationCode.length < 4) {
      setSmsError('인증번호를 입력해주세요');
      return;
    }

    setIsVerifyingSms(true);
    setSmsError('');

    try {
      // 로컬 개발 환경: 임시 인증번호 0000 허용
      const isDevelopment = process.env.REACT_APP_ENV === 'development' ||
                            process.env.NODE_ENV === 'development' ||
                            window.location.hostname === 'localhost';

      if (isDevelopment && verificationCode === '0000') {
        console.log('🔧 개발 모드: 임시 인증번호(0000)로 인증 통과');
        setSmsVerified(true);
        setSmsSentMessage('');
        setSmsError('');

        // 모바일에서 SMS 인증 완료 후 이름 입력 단계로
        if (isMobile) {
          setSlideDirection('next');
          setMobileStep(4);
          updateHistoryState(4);
        }
        return;
      }

      // 실제 SMS 인증 검증
      await smsAPI.verifyCode(formData.phone, verificationCode);
      setSmsVerified(true);
      setSmsSentMessage('');
      setSmsError('');

      // 모바일에서 SMS 인증 완료 후 이름 입력 단계로
      if (isMobile) {
        setSlideDirection('next');
        setMobileStep(4);
        updateHistoryState(4);
      }
    } catch (error) {
      console.error('SMS 검증 오류:', error);
      if (error.response?.data?.message) {
        setSmsError(error.response.data.message);
      } else {
        setSmsError('인증번호가 일치하지 않습니다.');
      }
    } finally {
      setIsVerifyingSms(false);
    }
  };

  // SMS 재발송
  const handleResendSms = async () => {
    if (resendCooldown > 0) {
      return; // 쿨다운 중에는 재발송 불가
    }
    await handleSendSms();
  };

  // Enter 키 처리
  const handlePhoneKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handlePhoneCheck();
    }
  };

  // SMS 인증 코드 Enter 키 처리
  const handleSmsKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleVerifySms();
    }
  };

  // 이름 Enter 키 처리 (모바일)
  const handleNameKeyPress = (e) => {
    if (e.key === 'Enter' && isMobile && formData.name.trim()) {
      e.preventDefault();
      handleMobileNextStep();
    }
  };

  // 폼 유효성 검사
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = '이름을 입력해주세요';
    }

    if (!formData.treatment) {
      newErrors.treatment = '시술을 선택해주세요';
    }

    // 신규 고객일 경우 필수 동의 확인
    if (!isExistingCustomer) {
      if (!formData.agreePrivacy) {
        newErrors.agreePrivacy = '개인정보 수집 및 이용에 동의해주세요';
      }
      if (!formData.agreeNotification) {
        newErrors.agreeNotification = '예약 관련 알림 수신에 동의해주세요';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 예약 확정 핸들러
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);
    setSubmitMessage('');

    try {
      // 🔍 제출 직전 가용성 최종 확인 (경쟁 조건 방지)
      const formattedDate = formatDate(selectedDate);
      const slotsResponse = await reservationAPI.getAvailableSlots(formattedDate);
      const currentAvailableSlots = slotsResponse.data.availableSlots;

      // 선택한 시간이 아직 가능한지 확인
      if (!currentAvailableSlots.includes(selectedTime)) {
        setSubmitStatus('error');
        setSubmitMessage(`죄송합니다. 선택하신 시간(${selectedTime})은 방금 다른 고객이 예약했습니다. 다른 시간을 선택해주세요.`);

        // 가용 시간 업데이트
        setAvailableSlots(currentAvailableSlots);
        setSelectedTime(''); // 선택 초기화

        // 모바일이면 시간 선택 단계로 이동
        if (isMobile) {
          setSlideDirection('prev');
          setMobileStep(2);
          window.history.back();
        }

        setIsSubmitting(false);
        return;
      }

      const reservationTime = combineDateAndTime(selectedDate, selectedTime);
      const selectedTreatment = getSelectedTreatment();

      const requestData = {
        name: formData.name,
        phone: formData.phone,
        treatment: formData.treatment,
        reservationTime: reservationTime,
        requestMemo: formData.requestMemo || '',
      };

      // 신규 고객일 경우 동의 정보 추가
      if (!isExistingCustomer) {
        requestData.agreePrivacy = formData.agreePrivacy;
        requestData.agreeNotification = formData.agreeNotification;
        requestData.agreeMarketing = formData.agreeMarketing;
      }

      const response = await reservationAPI.create(requestData);

      // 예약 완료 정보 저장
      setCompletedReservation({
        id: response.data,
        date: selectedDate,
        time: selectedTime,
        treatment: formData.treatment,
        treatmentInfo: selectedTreatment,
        name: formData.name,
        phone: formData.phone,
        isNewCustomer: !isExistingCustomer,
      });

      if (isMobile) {
        setSlideDirection('next');
        setMobileStep(9);
        updateHistoryState(9);
      } else {
        setStep(3);
      }

    } catch (error) {
      setSubmitStatus('error');

      if (error.response?.data?.message) {
        // 백엔드에서 중복 예약 에러 처리
        const errorMsg = error.response.data.message;

        if (errorMsg.includes('이미 예약') || errorMsg.includes('중복')) {
          setSubmitMessage(`선택하신 시간대는 이미 예약되었습니다. 다른 시간을 선택해주세요.`);

          // 가용 시간 다시 조회
          try {
            const formattedDate = formatDate(selectedDate);
            const slotsResponse = await reservationAPI.getAvailableSlots(formattedDate);
            setAvailableSlots(slotsResponse.data.availableSlots);
            setSelectedTime('');

            if (isMobile) {
              setSlideDirection('prev');
              setMobileStep(2);
            }
          } catch (refreshError) {
            console.error('가용 시간 재조회 실패:', refreshError);
          }
        } else {
          setSubmitMessage(errorMsg);
        }
      } else if (error.response?.data?.details) {
        const details = error.response.data.details;
        setErrors(details);
        setSubmitMessage('입력 정보를 확인해주세요.');
      } else {
        setSubmitMessage('예약 중 오류가 발생했습니다. 다시 시도해주세요.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // 새 예약 시작
  const handleNewReservation = () => {
    setStep(1);
    setMobileStep(1);
    setSelectedDate(null);
    setSelectedTime('');
    setAvailableSlots([]);
    setSlotsError('');
    setCompletedReservation(null);
    resetFormState();
    if (isMobile) {
      window.history.replaceState({ mobileStep: 1 }, '', '#step-1');
    }
  };

  // 모바일 단계별 "다음" 버튼 활성화 여부
  const canProceedMobile = () => {
    switch (mobileStep) {
      case 1: return !!selectedDate;
      case 2: return !!selectedTime;
      case 3:
        // 휴대폰 확인 완료 AND (기존 고객 OR SMS 인증 완료)
        return phoneVerified && (isExistingCustomer || smsVerified);
      case 4: return !!formData.name.trim();
      case 5: return !!formData.treatment;
      case 6: return true; // 요청사항은 선택사항
      case 7: return true; // 예약 확인 단계는 항상 통과
      case 8:
        // 동의서 단계 (신규 고객만 표시됨): 필수 동의 확인
        return formData.agreePrivacy && formData.agreeNotification;
      default: return false;
    }
  };

  // ========== 모바일 렌더링 함수들 ==========

  // 모바일 프로그레스 바
  const renderMobileProgress = () => (
    <div className="mobile-progress">
      <div className="mobile-progress-bar">
        <div
          className="mobile-progress-fill"
          style={{ width: `${(mobileStep / 9) * 100}%` }}
        />
      </div>
      <div className="mobile-progress-text">
        <span className="mobile-progress-current">{mobileStep}</span>
        <span className="mobile-progress-divider">/</span>
        <span className="mobile-progress-total">9</span>
      </div>
    </div>
  );

  // 모바일 Step 1: 날짜 선택
  const renderMobileDateStep = () => (
    <div className={`mobile-step-content slide-${slideDirection}`}>
      <div className="mobile-step-header">
        <h2 className="mobile-step-title">{mobileStepInfo[1].title}</h2>
        <p className="mobile-step-description">{mobileStepInfo[1].description}</p>
      </div>
      <Calendar
        selectedDate={selectedDate}
        onSelectDate={handleDateSelect}
      />
    </div>
  );

  // 모바일 Step 2: 시간 선택
  const renderMobileTimeStep = () => (
    <div className={`mobile-step-content slide-${slideDirection}`}>
      <div className="mobile-step-header">
        <h2 className="mobile-step-title">{mobileStepInfo[2].title}</h2>
        <p className="mobile-step-description">{mobileStepInfo[2].description}</p>
      </div>

      {/* 선택된 날짜 표시 */}
      <div className="mobile-selected-info">
        <span className="mobile-selected-label">선택한 날짜</span>
        <span className="mobile-selected-value">
          {formatDateKorean(selectedDate)} ({getDayOfWeekKorean(selectedDate)})
        </span>
      </div>

      <TimeSlots
        selectedDate={selectedDate}
        selectedTime={selectedTime}
        onSelectTime={handleTimeSelect}
        availableSlots={availableSlots}
        isLoading={isLoadingSlots}
        error={slotsError}
        onRetry={() => fetchAvailableSlots(selectedDate)}
      />
    </div>
  );

  // 모바일 Step 3: 휴대폰 번호 + SMS 인증
  const renderMobilePhoneStep = () => (
    <div className={`mobile-step-content slide-${slideDirection}`}>
      <div className="mobile-step-header">
        <h2 className="mobile-step-title">{mobileStepInfo[3].title}</h2>
        <p className="mobile-step-description">{mobileStepInfo[3].description}</p>
      </div>

      <div className="mobile-form-section">
        {/* 중복 예약 경고 */}
        {showDuplicateWarning && duplicateReservation && (
          <div className="duplicate-warning">
            <div className="duplicate-warning-header">
              <h3 className="duplicate-warning-title">중복 예약 안내</h3>
            </div>
            <p className="duplicate-warning-message">
              선택하신 날짜({formatDateKorean(selectedDate)})에 이미 예약이 있습니다.
            </p>
            <div className="duplicate-reservation-info">
              <div className="duplicate-info-row">
                <span className="duplicate-info-label">시간</span>
                <span className="duplicate-info-value">
                  {new Date(duplicateReservation.reservationTime).toLocaleTimeString('ko-KR', {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                  })}
                </span>
              </div>
              <div className="duplicate-info-row">
                <span className="duplicate-info-label">시술</span>
                <span className="duplicate-info-value">{duplicateReservation.treatment}</span>
              </div>
              <div className="duplicate-info-row">
                <span className="duplicate-info-label">상태</span>
                <span className="duplicate-info-value">
                  {duplicateReservation.status === 'PENDING' ? '예약 대기' : '예약 확정'}
                </span>
              </div>
            </div>
            <div className="duplicate-warning-actions">
              <Button
                type="button"
                variant="outline"
                size="medium"
                fullWidth
                onClick={() => {
                  setShowDuplicateWarning(false);
                  setDuplicateReservation(null);
                  setFormData(prev => ({ ...prev, phone: '' }));
                }}
              >
                다른 날짜 선택
              </Button>
              <Button
                type="button"
                variant="primary"
                size="medium"
                fullWidth
                onClick={handleContinueDespiteDuplicate}
              >
                추가 예약하기
              </Button>
            </div>
          </div>
        )}

        {!showDuplicateWarning && !phoneVerified ? (
          // 휴대폰 번호 입력 및 확인
          <>
            <div className="input-wrapper">
              <label htmlFor="phone" className="input-label">
                휴대폰 번호
                <span className="input-required">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                onKeyPress={handlePhoneKeyPress}
                placeholder="010-1234-5678"
                className={`input-field mobile-input ${errors.phone ? 'error' : ''}`}
                maxLength={13}
                autoComplete="tel"
                autoFocus
              />
              {errors.phone && (
                <span className="input-error-message">{errors.phone}</span>
              )}
            </div>

            <Button
              type="button"
              variant="primary"
              size="large"
              fullWidth
              onClick={handlePhoneCheck}
              loading={isCheckingPhone}
              disabled={isCheckingPhone || !formData.phone}
            >
              {isCheckingPhone ? '확인 중...' : '확인'}
            </Button>
          </>
        ) : !showDuplicateWarning && !isExistingCustomer ? (
          // 신규 고객 - SMS 인증
          <>
            {/* 간결한 환영 메시지 */}
            <div className="compact-welcome-message">
              <span className="compact-welcome-text">{welcomeMessage}</span>
            </div>

            {/* 확인된 휴대폰 번호 표시 (개선) */}
            <div className="verified-phone-card">
              <div className="verified-phone-info">
                <span className="verified-phone-label">휴대폰 번호</span>
                <span className="verified-phone-number">{formData.phone}</span>
              </div>
              <button
                type="button"
                className="verified-phone-change-btn"
                onClick={handleResetPhone}
              >
                변경
              </button>
            </div>

            {/* SMS 인증 코드 입력 */}
            {!smsVerified && (
              <>
                <div className="input-wrapper">
                  <label htmlFor="verificationCode" className="input-label">
                    인증번호
                    <span className="input-required">*</span>
                  </label>
                  <input
                    type="text"
                    id="verificationCode"
                    name="verificationCode"
                    value={verificationCode}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      setVerificationCode(value);
                      setSmsError('');
                    }}
                    onKeyPress={handleSmsKeyPress}
                    placeholder="인증번호 입력 (로컬: 0000)"
                    className={`input-field mobile-input ${smsError ? 'error' : ''}`}
                    maxLength={6}
                    autoFocus
                  />
                  {smsError && (
                    <span className="input-error-message">{smsError}</span>
                  )}
                  {smsSentMessage && !smsError && (
                    <span className="input-help-message">{smsSentMessage}</span>
                  )}
                </div>

                <Button
                  type="button"
                  variant="primary"
                  size="large"
                  fullWidth
                  onClick={handleVerifySms}
                  loading={isVerifyingSms}
                  disabled={isVerifyingSms || verificationCode.length < 4}
                >
                  {isVerifyingSms ? '확인 중...' : '인증하기'}
                </Button>

                <button
                  type="button"
                  className="resend-link-btn"
                  onClick={handleResendSms}
                  disabled={resendCooldown > 0 || isSendingSms}
                >
                  {resendCooldown > 0
                    ? `인증번호 재발송 (${Math.floor(resendCooldown / 60)}:${String(resendCooldown % 60).padStart(2, '0')})`
                    : isSendingSms
                    ? '발송 중...'
                    : '인증번호가 오지 않나요? 재발송'}
                </button>
              </>
            )}

            {/* SMS 인증 완료 */}
            {smsVerified && (
              <div className="sms-verified-message">
                <span>인증이 완료되었습니다</span>
              </div>
            )}
          </>
        ) : (
          // 기존 고객 - 바로 다음 단계로 진행 가능
          <div className="existing-customer-info">
            {/* 간결한 환영 메시지 */}
            <div className="compact-welcome-message existing">
              <div className="compact-welcome-content">
                <span className="compact-welcome-text">{welcomeMessage}</span>
                {visitCount > 0 && (
                  <span className="compact-visit-count">{visitCount}번째 방문</span>
                )}
              </div>
            </div>

            {/* 확인된 휴대폰 번호 표시 (개선) */}
            <div className="verified-phone-card">
              <div className="verified-phone-info">
                <span className="verified-phone-label">휴대폰 번호</span>
                <span className="verified-phone-number">{formData.phone}</span>
              </div>
              <button
                type="button"
                className="verified-phone-change-btn"
                onClick={handleResetPhone}
              >
                변경
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // 모바일 Step 4: 이름 입력
  const renderMobileNameStep = () => (
    <div className={`mobile-step-content slide-${slideDirection}`}>
      <div className="mobile-step-header">
        <h2 className="mobile-step-title">{mobileStepInfo[4].title}</h2>
        <p className="mobile-step-description">{mobileStepInfo[4].description}</p>
      </div>

      {/* 환영 메시지 */}
      {welcomeMessage && (
        <div className={`welcome-message ${isExistingCustomer ? 'existing' : 'new'}`}>
          <div className="welcome-text">
            <span className="welcome-greeting">{welcomeMessage}</span>
          </div>
        </div>
      )}

      <div className="mobile-form-section">
        <div className="input-wrapper">
          <label htmlFor="name" className="input-label">
            이름
            <span className="input-required">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            onKeyPress={handleNameKeyPress}
            placeholder="이름을 입력해주세요"
            className={`input-field mobile-input ${errors.name ? 'error' : ''}`}
            autoComplete="name"
            autoFocus
          />
          {errors.name && (
            <span className="input-error-message">{errors.name}</span>
          )}
        </div>
      </div>
    </div>
  );

  // 모바일 Step 5: 시술 선택
  const renderMobileTreatmentStep = () => (
    <div className={`mobile-step-content slide-${slideDirection}`}>
      <div className="mobile-step-header treatment-step-header">
        <h2 className="mobile-step-title">{mobileStepInfo[5].title}</h2>

        {/* 첫 방문 할인 배너 */}
        {isFirstVisit() && (
          <div className="first-visit-banner mobile">
            <span className="banner-icon">🎉</span>
            <div className="banner-content">
              <span className="banner-title">첫 방문 50% 할인 이벤트</span>
              <span className="banner-subtitle">모든 시술 반값 특가!</span>
            </div>
          </div>
        )}

        {isExistingCustomer && welcomeMessage ? (
          <div className="welcome-inline">
            <span className="welcome-greeting-sm">
              {welcomeMessage}{visitCount > 0 ? ` · ${visitCount}번째 방문` : ''}
            </span>
          </div>
        ) : (
          <p className="mobile-step-description">{mobileStepInfo[5].description}</p>
        )}
      </div>

      <div className="mobile-treatment-grid">
        {treatmentOptions.filter(o => o.value).map((option) => {
          const isSelected = formData.treatment === option.value;
          const isConsult = option.value === '상담 후 선택';
          const priceInfo = getTreatmentPriceInfo(option);

          return (
            <button
              key={option.value}
              type="button"
              className={`mobile-treatment-card ${isSelected ? 'selected' : ''} ${isConsult ? 'consult' : ''} ${priceInfo.showDiscount ? 'has-discount' : ''}`}
              onClick={() => {
                setFormData(prev => ({ ...prev, treatment: option.value }));
                setErrors(prev => ({ ...prev, treatment: '' }));
              }}
            >
              {option.category && (
                <span className="treatment-card-category">{option.category}</span>
              )}
              <span className="treatment-card-name">{option.label}</span>

              {/* 가격 표시 개선 */}
              {priceInfo.showPrice && option.price > 0 ? (
                <div className="treatment-card-price-wrapper">
                  {priceInfo.showDiscount ? (
                    <>
                      <span className="treatment-card-original-price">
                        {formatCurrency(priceInfo.originalPrice)}
                      </span>
                      <div className="treatment-card-discount-row">
                        <span className="treatment-card-discount-badge">
                          {priceInfo.discountRate}%
                        </span>
                        <span className="treatment-card-final-price">
                          {formatCurrency(priceInfo.finalPrice)}
                        </span>
                      </div>
                    </>
                  ) : (
                    <span className="treatment-card-price">
                      {formatCurrency(priceInfo.finalPrice)}
                    </span>
                  )}
                </div>
              ) : isConsult ? (
                <span className="treatment-card-consult">상담 후 결정</span>
              ) : null}

              {isSelected && <span className="treatment-card-check">✓</span>}
            </button>
          );
        })}
      </div>
    </div>
  );

  // 모바일 Step 6: 요청사항
  const renderMobileRequestStep = () => (
    <div className={`mobile-step-content slide-${slideDirection}`}>
      <div className="mobile-step-header">
        <h2 className="mobile-step-title">{mobileStepInfo[6].title}</h2>
        <p className="mobile-step-description">{mobileStepInfo[6].description}</p>
      </div>

      {/* 요청사항 */}
      <div className="mobile-form-section">
        <div className="input-wrapper">
          <label htmlFor="requestMemo" className="input-label">
            요청사항
            <span className="input-optional">(선택)</span>
          </label>
          <textarea
            id="requestMemo"
            name="requestMemo"
            value={formData.requestMemo}
            onChange={handleChange}
            placeholder="원장님께 전달할 사항이 있으면 입력해주세요"
            className="input-field textarea-field"
            rows={4}
            maxLength={500}
          />
          <div className="char-count">
            {formData.requestMemo.length}/500
          </div>
        </div>

        {/* 작성 안내 */}
        <div className="request-memo-guide">
          <p className="guide-text">피부 상태나 궁금한 점을 작성해주세요</p>
        </div>
      </div>
    </div>
  );

  // 모바일 Step 7: 예약 확인
  const renderMobileReviewStep = () => (
    <div className={`mobile-step-content slide-${slideDirection}`}>
      <div className="mobile-step-header">
        <h2 className="mobile-step-title">{mobileStepInfo[7].title}</h2>
        <p className="mobile-step-description">{mobileStepInfo[7].description}</p>
      </div>

      {/* 예약 요약 */}
      <div className="mobile-reservation-summary">
        <div className="summary-row">
          <span className="summary-label">날짜</span>
          <span className="summary-value">
            {formatDateKorean(selectedDate)} ({getDayOfWeekKorean(selectedDate)})
          </span>
        </div>
        <div className="summary-row">
          <span className="summary-label">시간</span>
          <span className="summary-value">{selectedTime}</span>
        </div>
        <div className="summary-row">
          <span className="summary-label">예약자</span>
          <span className="summary-value">{formData.name}</span>
        </div>
        <div className="summary-row">
          <span className="summary-label">연락처</span>
          <span className="summary-value">{formData.phone}</span>
        </div>
        <div className="summary-row">
          <span className="summary-label">시술</span>
          <span className="summary-value">{formData.treatment}</span>
        </div>
        {getSelectedTreatment()?.price > 0 && (() => {
          const priceInfo = getTreatmentPriceInfo(getSelectedTreatment());
          return priceInfo.showPrice ? (
            <div className="summary-row highlight">
              <span className="summary-label">시술 비용</span>
              <div className="summary-price-wrapper">
                {priceInfo.showDiscount ? (
                  <>
                    <span className="summary-original-price">
                      {formatCurrency(priceInfo.originalPrice)}
                    </span>
                    <span className="summary-final-price">
                      {formatCurrency(priceInfo.finalPrice)}
                    </span>
                    {priceInfo.isFirstVisit && (
                      <span className="summary-discount-label">첫방문 50% 할인</span>
                    )}
                  </>
                ) : (
                  <span className="summary-value price">
                    {formatCurrency(priceInfo.finalPrice)}
                  </span>
                )}
              </div>
            </div>
          ) : null;
        })()}
        {formData.requestMemo && (
          <div className="summary-row">
            <span className="summary-label">요청사항</span>
            <span className="summary-value">{formData.requestMemo}</span>
          </div>
        )}
      </div>
    </div>
  );

  // 모바일 Step 8: 동의서 (신규 고객만)
  const renderMobileConsentStep = () => (
    <div className={`mobile-step-content slide-${slideDirection}`}>
      <div className="mobile-step-header">
        <h2 className="mobile-step-title">{mobileStepInfo[8].title}</h2>
        <p className="mobile-step-description">{mobileStepInfo[8].description}</p>
      </div>

      {/* 동의서 */}
      <div className="consent-section">
        <h3 className="consent-title">약관 동의</h3>
        <p className="consent-description">
          서비스 제공을 위해 아래 내용을 확인하고 동의해주세요
        </p>

        {/* 필수 동의 1: 개인정보 수집 및 이용 */}
        <div className={`consent-item ${errors.agreePrivacy ? 'error' : ''}`}>
          <label className="consent-label">
            <input
              type="checkbox"
              checked={formData.agreePrivacy}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, agreePrivacy: e.target.checked }));
                setErrors(prev => ({ ...prev, agreePrivacy: '' }));
              }}
              className="consent-checkbox"
            />
            <span className="consent-text">
              <span className="consent-required">필수</span> 개인정보 수집 및 이용에 동의합니다
            </span>
          </label>
          {errors.agreePrivacy && (
            <span className="consent-error-message">{errors.agreePrivacy}</span>
          )}
        </div>

        {/* 필수 동의 2: 예약 관련 알림 수신 */}
        <div className={`consent-item ${errors.agreeNotification ? 'error' : ''}`}>
          <label className="consent-label">
            <input
              type="checkbox"
              checked={formData.agreeNotification}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, agreeNotification: e.target.checked }));
                setErrors(prev => ({ ...prev, agreeNotification: '' }));
              }}
              className="consent-checkbox"
            />
            <span className="consent-text">
              <span className="consent-required">필수</span> 예약 관련 알림 수신에 동의합니다
            </span>
          </label>
          {errors.agreeNotification && (
            <span className="consent-error-message">{errors.agreeNotification}</span>
          )}
        </div>

        {/* 선택 동의: 마케팅 정보 수신 */}
        <div className="consent-item">
          <label className="consent-label">
            <input
              type="checkbox"
              checked={formData.agreeMarketing}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, agreeMarketing: e.target.checked }));
              }}
              className="consent-checkbox"
            />
            <span className="consent-text">
              <span className="consent-optional">선택</span> 마케팅 정보 수신에 동의합니다
            </span>
          </label>
        </div>

        <p className="consent-note">
          필요한 안내만 전송됩니다.
        </p>
      </div>

      {/* 에러 메시지 */}
      {submitStatus === 'error' && (
        <div className="submit-message submit-message-error">
          {submitMessage}
        </div>
      )}
    </div>
  );

  // 모바일 Step 9: 완료
  const renderMobileCompletionStep = () => (
    <div className={`mobile-step-content slide-${slideDirection}`}>
      <div className="completion-container">
        <div className="completion-header">
          <h2 className="completion-title">예약 완료</h2>
          <p className="completion-subtitle">
            예약 확인 문자를 전송해드립니다
          </p>
        </div>

        {completedReservation && (
          <div className="completion-details">
            <h3 className="details-title">예약 정보</h3>
            <div className="details-grid">
              <div className="detail-item">
                <span className="detail-label">예약 일시</span>
                <span className="detail-value">
                  {formatDateKorean(completedReservation.date)} ({getDayOfWeekKorean(completedReservation.date)}) {completedReservation.time}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">시술</span>
                <span className="detail-value">{completedReservation.treatment}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">예상 소요시간</span>
                <span className="detail-value">{completedReservation.treatmentInfo?.duration}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">예약자</span>
                <span className="detail-value">{completedReservation.name}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">연락처</span>
                <span className="detail-value">{completedReservation.phone}</span>
              </div>
            </div>
          </div>
        )}

        <div className="completion-actions">
          <Button
            variant="primary"
            size="large"
            fullWidth
            onClick={handleNewReservation}
          >
            새 예약하기
          </Button>
        </div>
      </div>
    </div>
  );

  // 모바일 하단 버튼
  const renderMobileBottomNav = () => {
    if (mobileStep === 9) return null; // 완료 단계

    // Step 3 (휴대폰)에서는 확인 버튼이 내부에 있으므로 하단 버튼 숨김
    if (mobileStep === 3) {
      return (
        <div className="mobile-bottom-nav">
          <Button
            type="button"
            variant="outline"
            size="large"
            onClick={handleMobilePrevStep}
          >
            이전
          </Button>
          <div style={{ flex: 1 }} />
        </div>
      );
    }

    return (
      <div className="mobile-bottom-nav">
        {mobileStep > 1 && (
          <Button
            type="button"
            variant="outline"
            size="large"
            onClick={handleMobilePrevStep}
            disabled={isSubmitting}
          >
            이전
          </Button>
        )}

        {/* Step 7(예약 확인): 기존 고객은 예약하기, 신규 고객은 다음 */}
        {mobileStep === 7 && isExistingCustomer ? (
          <Button
            type="button"
            variant="primary"
            size="large"
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={isSubmitting}
            className="mobile-submit-btn"
          >
            {isSubmitting ? '예약 중...' : '예약하기'}
          </Button>
        ) : mobileStep === 8 ? (
          /* Step 8(동의서): 예약하기 */
          <Button
            type="button"
            variant="primary"
            size="large"
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={!canProceedMobile() || isSubmitting}
            className="mobile-submit-btn"
          >
            {isSubmitting ? '예약 중...' : '예약하기'}
          </Button>
        ) : (
          /* 그 외: 다음 */
          <Button
            type="button"
            variant="primary"
            size="large"
            onClick={handleMobileNextStep}
            disabled={!canProceedMobile()}
            className="mobile-next-btn"
          >
            다음
          </Button>
        )}
      </div>
    );
  };

  // 모바일 렌더링
  const renderMobileView = () => (
    <div className="reservation-page mobile-view">
      {renderMobileProgress()}

      <div className="mobile-step-container" ref={stepContainerRef}>
        {mobileStep === 1 && renderMobileDateStep()}
        {mobileStep === 2 && renderMobileTimeStep()}
        {mobileStep === 3 && renderMobilePhoneStep()}
        {mobileStep === 4 && renderMobileNameStep()}
        {mobileStep === 5 && renderMobileTreatmentStep()}
        {mobileStep === 6 && renderMobileRequestStep()}
        {mobileStep === 7 && renderMobileReviewStep()}
        {mobileStep === 8 && renderMobileConsentStep()}
        {mobileStep === 9 && renderMobileCompletionStep()}
      </div>

      {renderMobileBottomNav()}

      {/* SMS 발송 토스트 알림 */}
      {showSmsToast && (
        <div className="sms-toast">
          <span className="sms-toast-text">{smsSentMessage}</span>
        </div>
      )}
    </div>
  );

  // ========== PC 렌더링 (기존 코드 유지) ==========
  const renderDesktopView = () => (
    <div className="reservation-page desktop-view">
      {/* Progress Indicator */}
      <div className="progress-indicator">
        <div className={`progress-step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
          <div className="progress-circle">{step > 1 ? '✓' : '1'}</div>
          <span className="progress-label">날짜·시간</span>
        </div>
        <div className={`progress-line ${step > 1 ? 'active' : ''}`}></div>
        <div className={`progress-step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
          <div className="progress-circle">{step > 2 ? '✓' : '2'}</div>
          <span className="progress-label">정보 입력</span>
        </div>
        <div className={`progress-line ${step > 2 ? 'active' : ''}`}></div>
        <div className={`progress-step ${step >= 3 ? 'active' : ''}`}>
          <div className="progress-circle">3</div>
          <span className="progress-label">완료</span>
        </div>
      </div>

      {/* Step 1: 날짜 및 시간 선택 */}
      {step === 1 && (
        <div className="step-container">
          <div className="step-header">
            <h2 className="step-title">예약 날짜와 시간을 선택해주세요</h2>
            <p className="step-description">
              원하시는 날짜를 선택하신 후, 시간을 선택해주세요
            </p>
          </div>

          <Calendar
            selectedDate={selectedDate}
            onSelectDate={handleDateSelect}
          />

          {selectedDate && (
            <TimeSlots
              selectedDate={selectedDate}
              selectedTime={selectedTime}
              onSelectTime={handleTimeSelect}
              availableSlots={availableSlots}
              isLoading={isLoadingSlots}
              error={slotsError}
              onRetry={() => fetchAvailableSlots(selectedDate)}
            />
          )}

          {selectedDate && selectedTime && (
            <div className="step-actions">
              <Button
                variant="primary"
                size="large"
                fullWidth
                onClick={handleNextStep}
              >
                다음 단계
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Step 2: 정보 입력 */}
      {step === 2 && (
        <div className="step-container">
          <div className="step-header">
            <h2 className="step-title">고객 정보를 입력해주세요</h2>
            <p className="step-description">
              {phoneVerified
                ? '예약을 확정하기 위한 정보를 입력해주세요'
                : '휴대폰 번호를 먼저 확인해주세요'}
            </p>
          </div>

          {/* 선택된 예약 일시 요약 */}
          <div className="reservation-summary">
            <div className="summary-content">
              <h3 className="summary-title">선택하신 예약 일시</h3>
              <div className="summary-details">
                <span className="summary-date">
                  {formatDateKorean(selectedDate)} ({getDayOfWeekKorean(selectedDate)})
                </span>
                <span className="summary-time">{selectedTime}</span>
              </div>
            </div>
            <button
              type="button"
              className="summary-edit-btn"
              onClick={handlePrevStep}
            >
              변경
            </button>
          </div>

          <form onSubmit={handleSubmit} className="reservation-form">
            {/* 중복 예약 경고 (PC) */}
            {showDuplicateWarning && duplicateReservation && (
              <div className="duplicate-warning desktop">
                <div className="duplicate-warning-header">
                  <span className="duplicate-warning-icon">⚠️</span>
                  <h3 className="duplicate-warning-title">중복 예약 안내</h3>
                </div>
                <p className="duplicate-warning-message">
                  선택하신 날짜({formatDateKorean(selectedDate)})에 이미 예약이 있습니다.
                </p>
                <div className="duplicate-reservation-info">
                  <div className="duplicate-info-row">
                    <span className="duplicate-info-label">시간</span>
                    <span className="duplicate-info-value">
                      {new Date(duplicateReservation.reservationTime).toLocaleTimeString('ko-KR', {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false
                      })}
                    </span>
                  </div>
                  <div className="duplicate-info-row">
                    <span className="duplicate-info-label">시술</span>
                    <span className="duplicate-info-value">{duplicateReservation.treatment}</span>
                  </div>
                  <div className="duplicate-info-row">
                    <span className="duplicate-info-label">상태</span>
                    <span className="duplicate-info-value">
                      {duplicateReservation.status === 'PENDING' ? '예약 대기' : '예약 확정'}
                    </span>
                  </div>
                </div>
                <div className="duplicate-warning-actions">
                  <Button
                    type="button"
                    variant="outline"
                    size="medium"
                    onClick={() => {
                      setShowDuplicateWarning(false);
                      setDuplicateReservation(null);
                      setFormData(prev => ({ ...prev, phone: '' }));
                    }}
                  >
                    다른 날짜 선택
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    size="medium"
                    onClick={handleContinueDespiteDuplicate}
                  >
                    추가 예약하기
                  </Button>
                </div>
              </div>
            )}

            {/* 휴대폰 번호 입력 */}
            <div className="phone-verification-section">
              {!showDuplicateWarning && !phoneVerified ? (
                <>
                  <div className="input-wrapper">
                    <label htmlFor="phone" className="input-label">
                      휴대폰 번호
                      <span className="input-required">*</span>
                    </label>
                    <div className="phone-input-row">
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        onKeyPress={handlePhoneKeyPress}
                        placeholder="010-1234-5678"
                        className={`input-field ${errors.phone ? 'error' : ''}`}
                        maxLength={13}
                        autoComplete="tel"
                      />
                      <Button
                        type="button"
                        variant="primary"
                        size="medium"
                        onClick={handlePhoneCheck}
                        loading={isCheckingPhone}
                        disabled={isCheckingPhone}
                      >
                        {isCheckingPhone ? '확인 중...' : '확인'}
                      </Button>
                    </div>
                    {errors.phone && (
                      <span className="input-error-message">{errors.phone}</span>
                    )}
                  </div>
                </>
              ) : !showDuplicateWarning ? (
                <div className="phone-verified-section">
                  <div className="verified-phone">
                    <span className="verified-label">휴대폰 번호</span>
                    <span className="verified-value">{formData.phone}</span>
                    <button
                      type="button"
                      className="change-phone-btn"
                      onClick={handleResetPhone}
                    >
                      변경
                    </button>
                  </div>
                </div>
              ) : null}
            </div>

            {/* 환영 메시지 */}
            {welcomeMessage && (
              <div className={`welcome-message ${isExistingCustomer ? 'existing' : 'new'}`}>
                <span className="welcome-emoji">{isExistingCustomer ? '💐' : '✨'}</span>
                <div className="welcome-text">
                  <span className="welcome-greeting">{welcomeMessage}</span>
                  {isExistingCustomer && visitCount > 0 && (
                    <span className="visit-count">{visitCount}번째 방문이시네요!</span>
                  )}
                  {!isExistingCustomer && (
                    <span className="deposit-notice">본인 확인을 위해 SMS 인증을 진행해주세요</span>
                  )}
                </div>
              </div>
            )}

            {/* 신규 고객 SMS 인증 */}
            {phoneVerified && !isExistingCustomer && !smsVerified && (
              <div className="sms-verification-section">
                <div className="input-wrapper">
                  <label htmlFor="verificationCode" className="input-label">
                    인증번호
                    <span className="input-required">*</span>
                  </label>
                  <div className="phone-input-row">
                    <input
                      type="text"
                      id="verificationCode"
                      name="verificationCode"
                      value={verificationCode}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        setVerificationCode(value);
                        setSmsError('');
                      }}
                      onKeyPress={handleSmsKeyPress}
                      placeholder="인증번호 입력 (로컬: 0000)"
                      className={`input-field ${smsError ? 'error' : ''}`}
                      maxLength={6}
                    />
                    <Button
                      type="button"
                      variant="primary"
                      size="medium"
                      onClick={handleVerifySms}
                      loading={isVerifyingSms}
                      disabled={isVerifyingSms || verificationCode.length < 4}
                    >
                      {isVerifyingSms ? '확인 중...' : '인증하기'}
                    </Button>
                  </div>
                  {smsError && (
                    <span className="input-error-message">{smsError}</span>
                  )}
                </div>

                <Button
                  type="button"
                  variant="outline"
                  size="small"
                  onClick={handleResendSms}
                  disabled={resendCooldown > 0 || isSendingSms}
                  style={{ marginTop: '8px' }}
                >
                  {resendCooldown > 0
                    ? `재발송 (${Math.floor(resendCooldown / 60)}:${String(resendCooldown % 60).padStart(2, '0')})`
                    : isSendingSms
                    ? '발송 중...'
                    : '인증번호 재발송'}
                </Button>
              </div>
            )}

            {/* SMS 인증 완료 메시지 */}
            {phoneVerified && !isExistingCustomer && smsVerified && (
              <div className="sms-verified-message">
                <span className="sms-verified-icon">✅</span>
                <span>인증이 완료되었습니다!</span>
              </div>
            )}

            {/* 휴대폰 번호 확인 후 표시되는 필드들 */}
            {phoneVerified && (isExistingCustomer || smsVerified) && (
              <>
                {/* 이름 입력 */}
                <div className="input-wrapper">
                  <label htmlFor="name" className="input-label">
                    이름
                    <span className="input-required">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="이름을 입력해주세요"
                    className={`input-field ${errors.name ? 'error' : ''}`}
                    disabled={isExistingCustomer}
                    autoComplete="name"
                  />
                  {errors.name && (
                    <span className="input-error-message">{errors.name}</span>
                  )}
                </div>

                {/* 시술 메뉴 선택 */}
                <div className="input-wrapper">
                  {/* 첫 방문 할인 배너 */}
                  {isFirstVisit() && (
                    <div className="first-visit-banner desktop">
                      <span className="banner-icon">🎉</span>
                      <div className="banner-content">
                        <span className="banner-title">첫 방문 고객 특별 혜택</span>
                        <span className="banner-subtitle">모든 시술 50% 할인</span>
                      </div>
                    </div>
                  )}

                  <label htmlFor="treatment" className="input-label">
                    시술 메뉴
                    <span className="input-required">*</span>
                  </label>
                  <select
                    id="treatment"
                    name="treatment"
                    value={formData.treatment}
                    onChange={handleChange}
                    className={`input-field select-field ${errors.treatment ? 'error' : ''}`}
                  >
                    {treatmentOptions.map((option) => {
                      const priceInfo = getTreatmentPriceInfo(option);

                      return (
                        <option key={option.value} value={option.value}>
                          {option.value
                            ? priceInfo.showPrice && priceInfo.showDiscount
                              ? `${option.label} - ${formatCurrency(priceInfo.finalPrice)} (${priceInfo.discountRate}% 할인)`
                              : priceInfo.showPrice && option.price > 0
                              ? `${option.label} - ${formatCurrency(option.price)}`
                              : option.label
                            : option.label}
                        </option>
                      );
                    })}
                  </select>
                  {errors.treatment && (
                    <span className="input-error-message">{errors.treatment}</span>
                  )}

                  {/* 선택된 시술 정보 표시 개선 */}
                  {formData.treatment && getSelectedTreatment()?.price > 0 && (() => {
                    const priceInfo = getTreatmentPriceInfo(getSelectedTreatment());
                    return priceInfo.showPrice ? (
                      <div className="treatment-info">
                        <div className="treatment-detail">
                          <span className="treatment-label">시술 비용</span>
                          <div className="treatment-price-display">
                            {priceInfo.showDiscount ? (
                              <>
                                <span className="original-price-strikethrough">
                                  {formatCurrency(priceInfo.originalPrice)}
                                </span>
                                <span className="discount-badge">{priceInfo.discountRate}%</span>
                                <span className="final-price-highlight">
                                  {formatCurrency(priceInfo.finalPrice)}
                                </span>
                              </>
                            ) : (
                              <span className="treatment-value price">
                                {formatCurrency(priceInfo.finalPrice)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : null;
                  })()}
                </div>

                {/* 요청사항 입력 */}
                <div className="input-wrapper">
                  <label htmlFor="requestMemo" className="input-label">
                    요청사항
                    <span className="input-optional">(선택)</span>
                  </label>
                  <textarea
                    id="requestMemo"
                    name="requestMemo"
                    value={formData.requestMemo}
                    onChange={handleChange}
                    placeholder="원장님께 전달할 사항이 있으면 입력해주세요"
                    className="input-field textarea-field"
                    rows={3}
                    maxLength={500}
                  />
                  <div className="char-count">
                    {formData.requestMemo.length}/500
                  </div>
                </div>

                {/* 신규 고객 동의서 */}
                {!isExistingCustomer && (
                  <div className="consent-section">
                    <h3 className="consent-title">잠깐만요! 마지막으로 확인해주세요 😊</h3>
                    <p className="consent-description">
                      더 나은 서비스를 위해 아래 내용에 동의해주시면 감사하겠습니다
                    </p>

                    {/* 필수 동의 1: 개인정보 수집 및 이용 */}
                    <div className={`consent-item ${errors.agreePrivacy ? 'error' : ''}`}>
                      <label className="consent-label">
                        <input
                          type="checkbox"
                          checked={formData.agreePrivacy}
                          onChange={(e) => {
                            setFormData(prev => ({ ...prev, agreePrivacy: e.target.checked }));
                            setErrors(prev => ({ ...prev, agreePrivacy: '' }));
                          }}
                          className="consent-checkbox"
                        />
                        <span className="consent-text">
                          <span className="consent-required">[필수]</span> 소중한 개인정보를 예약 처리를 위해 수집하고 이용하는 것에 동의해요
                        </span>
                      </label>
                      {errors.agreePrivacy && (
                        <span className="consent-error-message">{errors.agreePrivacy}</span>
                      )}
                    </div>

                    {/* 필수 동의 2: 예약 관련 알림 수신 */}
                    <div className={`consent-item ${errors.agreeNotification ? 'error' : ''}`}>
                      <label className="consent-label">
                        <input
                          type="checkbox"
                          checked={formData.agreeNotification}
                          onChange={(e) => {
                            setFormData(prev => ({ ...prev, agreeNotification: e.target.checked }));
                            setErrors(prev => ({ ...prev, agreeNotification: '' }));
                          }}
                          className="consent-checkbox"
                        />
                        <span className="consent-text">
                          <span className="consent-required">[필수]</span> 예약 확인, 일정 변경, 당일 리마인드 문자를 받을게요
                        </span>
                      </label>
                      {errors.agreeNotification && (
                        <span className="consent-error-message">{errors.agreeNotification}</span>
                      )}
                    </div>

                    {/* 선택 동의: 마케팅 정보 수신 */}
                    <div className="consent-item">
                      <label className="consent-label">
                        <input
                          type="checkbox"
                          checked={formData.agreeMarketing}
                          onChange={(e) => {
                            setFormData(prev => ({ ...prev, agreeMarketing: e.target.checked }));
                          }}
                          className="consent-checkbox"
                        />
                        <span className="consent-text">
                          <span className="consent-optional">[선택]</span> 특별한 이벤트나 할인 소식을 가끔 받아볼래요
                        </span>
                      </label>
                    </div>

                    <p className="consent-note">
                      💌 걱정마세요! 스팸 문자는 절대 보내지 않아요. 필요한 안내만 정성껏 보내드릴게요.
                    </p>
                  </div>
                )}

                {/* 에러 메시지 */}
                {submitStatus === 'error' && (
                  <div className="submit-message submit-message-error">
                    <span className="error-icon">⚠️</span>
                    {submitMessage}
                  </div>
                )}

                {/* 버튼 그룹 */}
                <div className="step-actions-group">
                  <Button
                    type="button"
                    variant="outline"
                    size="large"
                    onClick={handlePrevStep}
                    disabled={isSubmitting}
                  >
                    이전
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    size="large"
                    loading={isSubmitting}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? '예약 중...' : '예약 확정'}
                  </Button>
                </div>
              </>
            )}
          </form>
        </div>
      )}

      {/* Step 3: 예약 완료 */}
      {step === 3 && completedReservation && (
        <div className="step-container completion-container">
          <div className="completion-header">
            <div className="completion-icon">✅</div>
            <h2 className="completion-title">예약이 완료되었습니다!</h2>
            <p className="completion-subtitle">
              {completedReservation.isNewCustomer
                ? '첫 방문 감사드립니다. 예약 확인 문자가 발송됩니다.'
                : '항상 찾아주셔서 감사합니다. 예약 확인 문자가 발송됩니다.'}
            </p>
          </div>

          <div className="completion-details">
            <h3 className="details-title">예약 정보</h3>
            <div className="details-grid">
              <div className="detail-item">
                <span className="detail-label">예약 일시</span>
                <span className="detail-value">
                  {formatDateKorean(completedReservation.date)} ({getDayOfWeekKorean(completedReservation.date)}) {completedReservation.time}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">시술</span>
                <span className="detail-value">{completedReservation.treatment}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">예상 소요시간</span>
                <span className="detail-value">{completedReservation.treatmentInfo?.duration}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">예약자</span>
                <span className="detail-value">{completedReservation.name}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">연락처</span>
                <span className="detail-value">{completedReservation.phone}</span>
              </div>
            </div>
          </div>

          <div className="completion-actions">
            <Button
              variant="primary"
              size="large"
              fullWidth
              onClick={handleNewReservation}
            >
              새 예약하기
            </Button>
          </div>
        </div>
      )}

      {/* 예약 안내 */}
      {step !== 3 && (
        <div className="reservation-info">
          <h3 className="info-title">예약 안내</h3>
          <ul className="info-list">
            <li>시술 시간은 상황에 따라 변동될 수 있습니다.</li>
            <li>영업시간은 오전 10시부터 오후 10시까지입니다.</li>
            <li>일요일은 정기 휴무입니다.</li>
            <li>예약 변경 및 취소는 최소 하루 전 연락 바랍니다.</li>
            <li>문의: 010-7761-5730</li>
          </ul>
        </div>
      )}
    </div>
  );

  return (
    <Layout>
      {isMobile ? renderMobileView() : renderDesktopView()}

      {/* 데스크톱용 SMS 발송 토스트 알림 */}
      {!isMobile && showSmsToast && (
        <div className="sms-toast">
          <span className="sms-toast-text">{smsSentMessage}</span>
        </div>
      )}
    </Layout>
  );
};

export default ReservationPage;
